<template>
	<component
		:is="button.url ? AbsoluteInternalLink : 'button'"
		ref="buttonRef"
		:to="button.url"
		:type="button.url ? undefined : 'button'"
		class="
			flex
			items-center
			gap-1
			px-1.5
			xl:px-2
			py-3
			whitespace-nowrap
			text-center
			relative
			transition
			ease
			text-base
			after:transition
			after:ease
			after:absolute
			after:bottom-0
			after:inset-x-0
			after:h-[.1875rem]
			after:rounded-t-lg
			hover:after:bg-background-important
			focus-outline
		"
		:class="{'after:bg-background-important': visible, 'font-bold': fontBold}"
		:aria-expanded="visible"
		@click="handleClick(button.label)"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
	>
		{{ button.label }}
		<NuxtIcon
			v-if="button.items || button.dropdown"
			name="chevron-down"
			class="text-xl"
			:class="visible ? 'text-icon-primary' : 'text-icon-neutral'"
			filled
		/>
	</component>
	<Teleport to="#header">
		<Transition name="fade">
			<component
				:is="button.dropdown"
				v-if="visible && button.dropdown"
				ref="dropdownComponentRef"
				v-on-click-outside="[close, {ignore: [buttonRef]}]"
				:target-button="buttonRef"
				@update:click="close"
			/>
		</Transition>
		<Transition name="fade">
			<div
				v-if="visible && button.items"
				ref="dropdownRef"
				v-on-click-outside="[close, {ignore: [buttonRef]}]"
				:style="{...floatingStyles}"
				class="absolute z-[999] bg-background-primary rounded-b-2xl shadow-sm min-w-52 p-4"
				@mouseenter="handleDropdownMouseEnter"
				@mouseleave="handleDropdownMouseLeave"
			>
				<AbsoluteInternalLink
					v-for="(item, index) in button.items"
					:key="index"
					:to="item.url"
					class="block py-3 px-4 text-neutral-800 hover:bg-orange-100 rounded-lg whitespace-nowrap text-sm"
					:class="!item.disableActiveState && '[&.router-link-active]:bg-orange-100 [&.router-link-active]:text-neutral-1000'"
					@click="handleClick(`${button.label} | ${item.label}`, 'text_top_in_selector')"
				>
					{{ item.label }}
				</AbsoluteInternalLink>
			</div>
		</Transition>
	</Teleport>
</template>

<script setup lang="ts">
	import {useFloating, offset, shift} from '@floating-ui/vue';
	import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
	import {vOnClickOutside} from '@vueuse/components';
	import type {MenuLink} from '../../types/header';

	interface Props {
		button: MenuLink
		fontBold?: boolean
	}

	const props = defineProps<Props>();
	const AbsoluteInternalLink = resolveComponent('AbsoluteInternalLink');

	const [visible, toggleVisible] = useToggle(false);
	const buttonRef = ref<HTMLElement | null>(null);
	const dropdown = useTemplateRef<HTMLElement>('dropdownRef');
	const dropdownComponent = useTemplateRef<HTMLElement>('dropdownComponentRef');

	const {floatingStyles} = useFloating(buttonRef, dropdown, {middleware: [offset(4), shift({padding: 20})]});

	const {activate: activateFocusTrap, deactivate: deactivateFocusTrap} = useFocusTrap(dropdownComponent, {allowOutsideClick: true});

	const isHovering = ref(false);
	const {start: startCloseTimeout, stop: stopCloseTimeout} = useTimeoutFn(() => {
		if (!isHovering.value) {
			visible.value = false;
		}
	}, 150);

	const isLinkWithItems = computed(() => props.button.url && props.button.items);

	onKeyStroke('Escape', close);

	watch(visible, async (newValue) => {
		if (props.button.dropdown) {
			await nextTick();
			if (newValue) {
				activateFocusTrap();
			} else {
				deactivateFocusTrap();
			}
		}
	});

	function handleClick(label: string, content = 'hm_selector_top') {
		if (!props.button.url || props.button.dropdown) {
			toggleVisible();
		} else {
			close();
		}
		trackHeaderEvent(content, label);
	}

	function handleMouseEnter() {
		if (isLinkWithItems.value) {
			stopCloseTimeout();
			isHovering.value = true;
			visible.value = true;
		}
	}

	function handleMouseLeave() {
		if (isLinkWithItems.value) {
			isHovering.value = false;
			startCloseTimeout();
		}
	}

	function handleDropdownMouseEnter() {
		if (isLinkWithItems.value) {
			stopCloseTimeout();
			isHovering.value = true;
		}
	}

	function handleDropdownMouseLeave() {
		if (isLinkWithItems.value) {
			isHovering.value = false;
			startCloseTimeout();
		}
	}

	function close() {
		if (props.button.dropdown) {
			deactivateFocusTrap();
		}
		stopCloseTimeout();
		isHovering.value = false;
		visible.value = false;
	}
</script>
